import getMeasureAcronym from './MeasureAcronym'
import TimepointComposer from './TimepointComposer'
import { VALUE_TYPES } from 'enums/ENUMS'
import * as _ from 'lodash'

// Universal value composer.	Accepts any whole cell value object and returns
// a compiled string from the various components of the value, depending on the type.

interface ComposerOptions {
	includeNumber?: boolean,
	includePercent?: boolean,
	includeVariance?: boolean,
	includeVarianceType?: boolean,
	includeRanges?: boolean,
	includeUnit?: boolean,
	includeUnitOnRange?: boolean,
	includeRateLabels?: boolean, // rate
	includeRateCount?: boolean, // rate
	includeRateParticipants?: boolean, // rate
	includeCumulativeRate?: boolean, // rate
	includeTotalPersonTime?: boolean,
	rangeTypeOptional?: boolean,
	includeAMType?: boolean,
	includeP?: boolean,
	includePValue?: boolean,
}

const DEFAULTS = {
	includeNumber: true,
	includePercent: true,
	includeVariance: true,
	includeVarianceType: true,
	includeRanges: true,
	includeUnit: false,
	includeUnitOnRange: false,
	includeRateLabels: true, // rate
	includeRateCount: true, // rate
	includeRateParticipants: true, // rate
	includeCumulativeRate: true, // rate
	includeTotalPersonTime: true, // rate
	rangeTypeOptional: false,
	includeAMType: false,
	includeP: true,
	includePValue: true,
}

export default class ValueComposer {
	constructor() {}

	static compose(value: any, options: ComposerOptions = DEFAULTS): string {
		if (value == null) return ''
		if (typeof value === 'string') return value
		if (typeof value !== 'object') return value + ''
		if (value.Status) return value.Status // NR
		if (value.Value) return value.Value + '' // covers most primitives

		options = _.assign({}, DEFAULTS, options)

		const type = value.Type || value?.Row?.Type
		if (!type)
			return ''
		if (!ValueComposer[type]) {
			console.warn('ValueComposer does not have support for this value type.', value)
			return ''
		}
		return ValueComposer[type](value, options)
	}

	static Boolean(value: any, options: ComposerOptions = DEFAULTS): string {
		return value.Value ? 'Yes' : 'No'
	}

	static String(value: any, options: ComposerOptions = DEFAULTS): string {
		return value.Value
	}

	static Memo(value: any, options: ComposerOptions = DEFAULTS): string {
		return value.Value
	}

	static YesNoNa(value: any, options: ComposerOptions = DEFAULTS): string {
		return value.Value
	}

	// Contact
	// StudyReference
	// StudyDoc
	// Facility
	// LookupTable
	static EventCounts(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Integer(value, options)
	}

	static Integer(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Decimal(value, options)
	}

	static Decimal(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if ((value.Number != null) && options.includeNumber) {
			gestalt += value.Number
		}
		if (value.Numerator || value.Denominator) {
			gestalt += ValueComposer.Fraction(value, options)
		}
		return gestalt
	}

	// still haven't resolved issue of low/high NRs
	static UnitRange(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Range(value, options)
	}

	// still haven't resolved issue of low/high NRs
	static UnitsRange(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Range(value, options)
	}

	static UnitValue(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.Number != null)
			gestalt += value.Number
		if (options.includeUnit && (value?.Row?.Unit || value.Unit)) {
			if (gestalt)
				gestalt += ' '
			gestalt += ValueComposer.Unit(value, options)
		}
		if (value.Range && options.includeRanges) {
			if (gestalt)
				gestalt += ' '
			gestalt += '[' + ValueComposer.RangeComposer(value.Range, options) + ']'
		}
		if (!gestalt)
			gestalt = 'NR'
		return gestalt
	}
	static Units(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.UnitValue(value, options)
	}

	static Unit(value: any, options: ComposerOptions = DEFAULTS): string {
		const unit = value?.Row?.Unit || value.Unit
		if (value.Per) {
			return unit + '/' + ValueComposer.Unit(value.Per)
		} else {
			return unit
		}
	}

	// binary
	static Participant(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if ((value.Number != null) && options.includeNumber) {
			gestalt += value.Number
		}
		let extras = []
		if ((value.Percent != null) && options.includePercent) {
			extras.push(ValueComposer.Percent(value.Percent) + '%')
		}
		extras.push(...ValueComposer.Ranges(value, options))
		if (extras.length > 0) {
			gestalt += gestalt ? ' (' + extras.join(', ') + ')' : extras.join(', ')
		}
		return gestalt
	}

	static Percent(value: number): string {
		let gestalt = ''
		if ((value + '').length > 4) {
			if (value < 0.01)
				gestalt += '< 1'
			else
				gestalt += parseFloat(value.toFixed(2))
		} else
			gestalt += value
		return gestalt
	}

	static Ratio(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static Percentage(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static EstimatedProportion(value: any, options: ComposerOptions = DEFAULTS): string {
		// this continuous, but also have a Percent
		return ValueComposer.Continuous(value, options)
	}

	static Flex(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static Fixed(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static FixedDoseInterventionDose(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.Intervention) {
			gestalt += value.Intervention.Name.Gestalt
		}
		const dose = ValueComposer.UnitValue(value, options)
		if (dose) {
			if (gestalt) gestalt += ' '
			gestalt += dose
		}
		return gestalt
	}

	static FixedDoseIntervention(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.Doses) {
			const doses = value.Doses.map( value => ValueComposer.FixedDoseInterventionDose(value, options))
			gestalt += ' ' + doses.join(' / ')
		}
		return gestalt
	}

	static Fraction(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if ((!value.Numerator && !value.Denominator)) {
			return gestalt
		}
		if (value.Numerator) {
			gestalt += value.Numerator.Number
		} else {
			gestalt += 'NA'
		}
		gestalt += '/'
		if (value.Denominator) {
			gestalt += value.Denominator.Number
		} else {
			gestalt += 'NA'
		}
		return gestalt
	}

	static Range(value: any, options: ComposerOptions = DEFAULTS): string {
		if (value.Low == null && value.High == null) {
			if (value.Number != null)
				return value.Number + ''
			return 'NR – NR'
		}
		let gestalt = ''
		if (value?.Low?.Number != null) {
			gestalt += value.Low.Number
			const unit = value?.Low?.Unit || value.Unit || value?.Row?.Unit
			if (options?.includeUnitOnRange && unit) {
				gestalt += ' ' + unit
			}
		} else {
			gestalt = '?'
		}
		if (value.Low || value.High) {
			gestalt += ' – ' // this is an n-dash (&ndash;) - longer than hyphen
		}
		if (value?.High?.Number != null) {
			gestalt += value.High.Number
			const unit = value?.High?.Unit || value.Unit || value?.Row?.Unit
			if (options?.includeUnitOnRange && unit) {
				gestalt += ' ' + unit
			}
		} else {
			if (gestalt === '?') {
				gestalt += ' – '
			}
			gestalt += '?'
		}
		return gestalt
	}

	static Ranges(value?: any | null, options: ComposerOptions = DEFAULTS): string {
		if (value.Ranges && options.includeRanges) {
			return value.Ranges.map(range => ValueComposer.RangeComposer(range, options))
		}
		return []
	}

	static RangeComposer(value?: any | null, options: ComposerOptions = DEFAULTS): string {
		if (!value) return ''
		if (!value.Type && !options.rangeTypeOptional) {
			console.error('Range has no Type.', value)
		}
		let gestalt = ''
		if (!options.rangeTypeOptional || value.Type) {
			gestalt += ValueComposer.RangeType(value, options)
		}
		return gestalt + ' ' + ValueComposer.Range(value, options)
	}

	static RangeType(value?: any | null, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.Type === 'CI' || value.Type === 'Variance CI') {
			if (value.Percent)
				gestalt += value.Percent + '% '
			gestalt += value.Type
		} else if (value.Type === 'Percentile') {
			if (value.PercentLow)
				gestalt += value.PercentLow
			if (value.Percent) {
				if (value.PercentLow)
					gestalt += '–'
				gestalt += value.Percent
			}
			gestalt += 'th Percentile'
		} else {
			gestalt = (value.Type || 'NR')
		}
		return gestalt + ': '
	}

	static Continuous(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.IsUnknown)
			gestalt += '?'
		else if ((value.Number != null) && options.includeNumber) {
			if (value.Numerator || value.Denominator) {
				gestalt += ValueComposer.Fraction(value, options)
			} else {
				gestalt += value.Number
			}
			if (options.includeUnit && (value.Unit != null || value?.Row?.Unit != null)) {
				let unit = (value.Unit || value.Row.Unit)
				gestalt += unit === '%' ? '%' : (' ' + unit)
			}
		}
		let extras = []
		if (value.High != null || value.Low != null) {
			extras.push(ValueComposer.Range(value, options))
		}
		if ((value.SD != null) && options.includeVariance) {
			extras.push(ValueComposer.SD(value, options))
		}
		if ((value.SE != null) && options.includeVariance) {
			extras.push(ValueComposer.SE(value, options))
		}
		if ((value.Variance != null) && options.includeVariance) {
			extras.push(ValueComposer.Variance(value, options))
		}
		extras.push(...ValueComposer.Ranges(value, options))
		if (extras.length > 0) {
			gestalt += gestalt ? ' (' + extras.join(', ') + ')' : extras.join(', ')
		}
		return gestalt
	}

	static SD(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.SD != null && options.includeVariance) {
			if (options.includeVarianceType)
				gestalt += 'SD '
			gestalt += '± ' + value.SD
		}
		return gestalt
	}

	static SE(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.SE != null && options.includeVariance) {
			if (options.includeVarianceType)
				gestalt += 'SE '
			gestalt += '± ' + value.SE
		}
		return gestalt
	}

	static Variance(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		if (value.Variance != null && options.includeVariance) {
			if (options.includeVarianceType)
				gestalt += (value.VarianceType || 'Unknown') + ' '
			gestalt += '± ' + value.Variance
		}
		return gestalt
	}

	// continuous
	static Average(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static Median(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static Mean(value: any, options: ComposerOptions = DEFAULTS): string {
		return ValueComposer.Continuous(value, options)
	}

	static NPersons(value: any, options?: ComposerOptions): string {
		let gestalt = ''
		const mean = ValueComposer.Continuous(value, options)
		if (value.PrevalenceType)
			gestalt += value.PrevalenceType
		if (mean) {
			if (gestalt)
				gestalt += ' '
			gestalt += 'Prevalence: ' + mean
		}
		if (value.Participants) {
			if (gestalt)
				gestalt += ' '
			gestalt += 'n/%: ' + ValueComposer.Participant(value.Participants, options)
		}
		if (value.Count) {
			if (gestalt)
				gestalt += ' '
			gestalt += 'Count: ' + value.Count.Number
		}
		return gestalt
	}

	static Rate(value: any, options: ComposerOptions = DEFAULTS): string {
		let gestalt = ''
		const mean = ValueComposer.Continuous(value, options)
		if (options.includeRateLabels && mean)
			gestalt += 'Rate: '
		gestalt += mean

		if (value?.Count?.Number != null && options.includeRateCount) {
			if (Number.isFinite(value.Count.Number)) {
				if (gestalt)
					gestalt += '; '
				if (options.includeRateLabels)
					gestalt += 'Count: '
				gestalt += value.Count.Number
			}
		}

		if (value.Participants && options.includeRateParticipants) {
			const participants = ValueComposer.Participant(value.Participants, options)
			if (participants) {
				if (gestalt)
					gestalt += '; '
				if (options.includeRateLabels)
					gestalt += 'n: '
			}
			gestalt += participants
		}

		if (value.CumulativeRate && options.includeCumulativeRate) {
			if (gestalt)
				gestalt += '; '
			if (options.includeRateLabels && value.CumulativeRate.Number != null)
				gestalt += 'Cumulative Rate: '
			gestalt += ValueComposer.Continuous(value.CumulativeRate, options)
		}

		if (value.TotalPersonTime && options.includeTotalPersonTime) {
			if (Number.isFinite(value.TotalPersonTime.Number)) {
				if (gestalt)
					gestalt += '; '
				if (options.includeRateLabels)
					gestalt += 'Total Person-time: ' // should we indicate the rate type here?
				gestalt += ValueComposer.Continuous(value.TotalPersonTime, options)
			}
		}

		return gestalt
	}

	static Funding(value: any, options?: ComposerOptions): string {
		let gestalt = ''
		if (value?.FundingType?.Name?.Gestalt)
			gestalt = value.FundingType.Name.Gestalt
		if (value?.InstitutionType?.Name?.Gestalt) {
			if (gestalt)
				gestalt += ' '
			gestalt += value.InstitutionType.Name.Gestalt
		}
		return gestalt
	}

	static DateTime(value, options?: ComposerOptions): string {
		if (value == null || typeof value !== 'object') return ''
		let gestalt = ''
		if (value.Date) {
			const date = new Date(value.Date)
			gestalt += (date.getUTCMonth() + 1) + '/' + date.getUTCDate() + '/' + date.getUTCFullYear()
		} else {
			if (value.Month != null) {
				gestalt += value.Month
			} else if (value.Day) {
				gestalt += 'NR'
			}
			if (value.Day != null) {
				if (gestalt)
					gestalt += '/'
				gestalt += value.Day
			}
			if (value.Year != null) {
				if (gestalt)
					gestalt += '/'
				gestalt += value.Year
			}
		}
		return gestalt
	}

	static DateTimeRange(value: any, options?: ComposerOptions): string {
		let gestalt = ''
		if (value.From) {
			gestalt += ValueComposer.DateTime(value.From, options)
		} else if (value.Type !== '=')
			gestalt += 'NR'

		let end
		if (value.To) {
			end = ValueComposer.DateTime(value.To, options)
		} else if (value.Type !== '=')
			end = 'NR'

		if (end) {
			if (gestalt)
				gestalt += ' – '
			gestalt += end
		}
		return gestalt
	}

	static AssociationMeasure(value: any, options?: ComposerOptions): string {
		let gestalt = ''
		if (options.includeAMType && value.AMType) {
			gestalt += getMeasureAcronym(value.AMType)
		}
		if (value.Number != null) {
			gestalt += ' ' + value.Number
			if (options.includeUnit && value.Unit)
				gestalt += ' ' + value.Unit
		}

		let extras = []
		if ((value.SD != null) && options.includeVariance) {
			extras.push(ValueComposer.SD(value, options))
		}
		if ((value.SE != null) && options.includeVariance) {
			extras.push(ValueComposer.SE(value, options))
		}
		if ((value.Variance != null) && options.includeVariance) {
			extras.push(ValueComposer.Variance(value, options))
		}
		extras.push(...ValueComposer.Ranges(value, options))
		if (options.includePValue && value.PValue != null) {
			extras.push(ValueComposer.PValue(value, options))
		}
		if (extras.length > 0) {
			gestalt += gestalt ? ' (' + extras.join(', ') + ')' : extras.join(', ')
		}
		return gestalt
	}

	static TimePoint(value, options: ComposerOptions = DEFAULTS): string {
		let bufferWord = ''
		if (value?.Row?.Name)
			bufferWord = getBufferWord(value.Row.Name.UnboundName)
		return bufferWord + TimepointComposer.compose(value.TimePoint, options)
	}

	static PValue(value, options: ComposerOptions = DEFAULTS): string {
		if (!value) return ''
		let gestalt = ''
		if (value.PValue != null) {
			if (options.includeP)
				gestalt += 'p '
			if (value.POperator) {
				gestalt += value.POperator + ' '
			}
			gestalt += value.PValue
		} else if (value.Type === VALUE_TYPES.PVALUE) {
			if (value.IsSignificant != null) {
				if (value.IsSignificant)
					gestalt += 'Significant'
				else
					gestalt += 'Not Significant'
			}
		}
		return gestalt
	}

	// Cannot be automatically computed
	static SettingLocation(value, options?: ComposerOptions): string {
		if (value == null || typeof value !== 'object') return ''
		let gestalt = ''
		
		// NOTE: Type is in name, so doesn't need to be displayed in value
		// if (value?.LocationType?.Name)
		// 	if (value?.LocationType?.Gestalt)
		// 		gestalt += value.LocationType.Gestalt
		// 	else
		// 		gestalt += value.LocationType.Name.Gestalt
		if (value?.LocationName?.Name) {
			if (gestalt)
				gestalt += ': '
			if (value?.LocationName?.Gestalt)
				gestalt += value.LocationName.Gestalt
			else
				gestalt += value.LocationName.Name.Gestalt
		}
		if (value.Number != null || value.Percent != null) {
			if (gestalt) gestalt += ', '
			gestalt += ValueComposer.Participant(value, options)
		}
		return gestalt
	}

	static SettingLocationTotal(value, options?: ComposerOptions): string {
		if (value == null || typeof value !== 'object') return ''
		let gestalt = ''
		if (value?.Location?.Name)
			gestalt += value.Location.Name.Gestalt
		if (value.Total != null) {
			if (gestalt) gestalt += ' '
			gestalt += 'Total: '
			gestalt += value.Total
		}
		if (value.Number != null || value.Percent != null) {
			if (value.Total != null) gestalt += ','
			if (gestalt) gestalt += ' '
			gestalt += ValueComposer.Participant(value, options)
		}
		return gestalt
	}

	static StudyObjective(value: any, options?: ComposerOptions): string {
		let gestalt = ''
		if (value.Objective)
			gestalt += value.Objective
		if (value.Description) {
			if (gestalt)
				gestalt += ': '
			gestalt += value.Description
		}
		return gestalt
	}

	static SubgroupDescriptor(value: any, options?: ComposerOptions): string {
		return ''
	}

	static Statistics(...args): string {
		return ValueComposer.UncollectedValue(...args)
	}

	static UncollectedBinary(...args): string {
		return ValueComposer.UncollectedValue(...args)
	}

	static UncollectedContinuous(...args): string {
		return ValueComposer.UncollectedValue(...args)
	}

	static UncollectedCount(...args): string {
		return ValueComposer.UncollectedValue(...args)
	}

	static UncollectedRate(...args): string {
		return ValueComposer.UncollectedValue(...args)
	}

	static UncollectedKaplanMeier(...args): string {
		return ValueComposer.UncollectedValue(...args)
	}

	static UncollectedValue(value: any, options?: ComposerOptions): string {
		return (value.Intervention ? ValueComposer.UncollectedIntervention(value, options) : value.UncollectedType) || ''
	}

	static UncollectedIntervention(value: any, options?: ComposerOptions): string {
		let gestalt = ''
		if (value?.Intervention?.Name) {
			gestalt = value.Intervention.Name.Gestalt
		}
		return gestalt
	}
}

const composeValue = ValueComposer.compose
export {
	composeValue,
}

const getBufferWord = (rowName: string): string => {
	switch (rowName.toLowerCase()) {
		case 'timepoint':
			return 'for '
		case 'timepoint (non-duration)':
			return '@ '
	}
	return ''
}
